import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Loader2, Star, ShieldCheck, ArrowLeft, MessageSquare, ThumbsUp } from "lucide-react";

export default function AdminSurvey() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/admin/survey"), api.get("/admin/survey/summary")])
      .then(([r, s]) => { setRows(r.data); setSummary(s.data); })
      .catch((e) => setErr(e?.response?.status === 403 ? "You are not authorised to view this page." : "Failed to load."));
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <Link to="/dashboard" className="btn-ghost mb-6 inline-flex"><ArrowLeft size={16} /> Back to dashboard</Link>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-blue text-white flex items-center justify-center"><ShieldCheck size={20} /></div>
        <div>
          <div className="overline">Admin</div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-brand-blue">Survey Results</h1>
        </div>
      </div>
      <p className="text-brand-muted mb-8">Only you can view this page. Access is granted to the admin email in the environment.</p>

      {err && <div className="surface p-6 text-brand-orange">{err}</div>}

      {!err && !summary && <Loader2 className="animate-spin text-brand-green" />}

      {summary && summary.total === 0 && (
        <div className="surface p-8 text-center text-brand-muted">
          No survey responses yet. Share the <Link to="/survey" className="text-brand-green underline">survey link</Link> with parents.
        </div>
      )}

      {summary && summary.total > 0 && (
        <>
          <div className="grid md:grid-cols-4 gap-5 mb-10">
            <div className="surface p-6"><MessageSquare className="text-brand-green mb-3" /><div className="text-3xl font-heading font-bold text-brand-blue">{summary.total}</div><div className="text-sm text-brand-muted">Total responses</div></div>
            <div className="surface p-6"><Star className="text-brand-orange mb-3" fill="currentColor" /><div className="text-3xl font-heading font-bold text-brand-blue">{summary.avg_ux_rating}<span className="text-lg text-brand-muted"> / 5</span></div><div className="text-sm text-brand-muted">Avg UX rating</div></div>
            <div className="surface p-6"><ThumbsUp className="text-brand-green mb-3" /><div className="text-3xl font-heading font-bold text-brand-blue">{summary.avg_satisfaction}<span className="text-lg text-brand-muted"> / 5</span></div><div className="text-sm text-brand-muted">Avg satisfaction</div></div>
            <div className="surface p-6">
              <div className="text-xs text-brand-muted mb-2">Would recommend</div>
              {Object.entries(summary.would_recommend || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="text-brand-blue">{k}</span>
                  <span className="font-heading font-semibold text-brand-blue">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-10">
            <div className="surface p-6">
              <h3 className="font-heading font-semibold text-brand-blue mb-3">Navigation ease</h3>
              {Object.entries(summary.ease_of_navigation || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-brand-border text-sm">
                  <span className="text-brand-blue">{k}</span><span className="font-medium text-brand-green">{v}</span>
                </div>
              ))}
            </div>
            <div className="surface p-6">
              <h3 className="font-heading font-semibold text-brand-blue mb-3">Information helpful?</h3>
              {Object.entries(summary.information_helpful || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-brand-border text-sm">
                  <span className="text-brand-blue">{k}</span><span className="font-medium text-brand-green">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-brand-blue mb-4">All responses</h2>
          <div className="space-y-4">
            {rows?.map((r, i) => (
              <div key={i} className="surface p-5">
                <div className="flex flex-wrap gap-2 mb-2 text-xs">
                  <span className="chip">UX {r.ux_rating}/5</span>
                  <span className="chip-orange">Satisfaction {r.satisfaction}/5</span>
                  <span className="chip-blue">Nav: {r.ease_of_navigation}</span>
                  <span className="chip-blue">Info: {r.information_helpful}</span>
                  <span className="chip-blue">Recommend: {r.would_recommend}</span>
                </div>
                {r.suggestions && <p className="text-brand-blue text-sm mt-2 italic">"{r.suggestions}"</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(r.favourite_features || []).map((f) => <span key={f} className="chip">❤ {f}</span>)}
                  {(r.improvement_areas || []).map((f) => <span key={f} className="chip-orange">↑ {f}</span>)}
                </div>
                <div className="mt-3 text-xs text-brand-muted flex flex-wrap gap-3">
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                  {r.email && <span>· {r.email}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
